import { ExecutionPath, Node, QuestionAnswer } from '../models';
import { Connection, Shape } from 'diagram-js/lib/model';
import { isEmpty } from 'lodash';
import { NodeModdleElement } from '@ispm/shared/bpmn-io/bpmn-models';
import { ProcessInputDataUtils } from '@ispm-client/modules/process-executor/services/process-input-data-utils';
import { NodeType } from '@ispm/shared/bpmn-io/node-type';
import { ModelingUtil } from '@ispm/shared/bpmn-io/modeling-util';

export interface ShapesFromNodeToChildren {
  parentNode: Shape;
  connectionFromParentToGateway: Connection;
  gateway: Shape;
  connectionsFromGatewayToChildren: Connection[];
  children: Shape[];
}

export interface ShapesFromParentToAChild {
  parentShape: Shape;
  connectionFromParentToGateway: Connection;
  gateway: Shape;
  connectionsFromGatewayToAChild: Connection;
  childShape: Shape;
}

export class ExecutedPathUtils {

  /**
  1. n = 1 : returns currentUserSelection node or treatment node
  2. n = n-1 : going back from last executed before 1 till head/nei head
   3. there is a case where currentUserTaskHolder is nei entry, in such case return only it
**/
  public static getLastNExecutedNodes(executionPath: ExecutionPath, n: number): Array<string> {
    if (n < 1 || executionPath == null || executionPath === undefined) {
      return new Array<string>();
    }

    const lastExecutedNodeId: string = this.getLastExecutedNodeId(executionPath);
    let executedNodes: Array<string> = new Array();
    if (!(executionPath.currentUserTaskHolder && executionPath.currentUserTaskHolder.nodeType === NodeType.neighborhood_head) && !isEmpty(executionPath.executedNodes)) {
      const numberOfExecutedNodes = executionPath.executedNodes.length > n - 1 ? n - 1 : executionPath.executedNodes.length;
      executedNodes = ExecutedPathUtils.getLastExecutedNodesInCurrentLayer(numberOfExecutedNodes, executionPath.executedNodes);
    }
     return [...executedNodes, lastExecutedNodeId];
  }

  public static getLastExecutedNodeId(executionPath: ExecutionPath) {
    return executionPath.currentUserTaskHolder ? executionPath.currentUserTaskHolder.nodeId : executionPath.executedTreatmentTaskId;
  }

  public static getLastExecutedNodesInCurrentLayer(numberOfExecutedNodes: number, executedNodes: Node[]): Array<string> {
    let counter = 1;
    const nodeIds: Array<string> = new Array<string>();
    while (counter <= numberOfExecutedNodes && counter <= executedNodes.length) {
      const curIndex = executedNodes.length - counter;
      nodeIds.push(executedNodes[curIndex].nodeId);
      if (executedNodes[curIndex].nodeType === NodeType.neighborhood_head) {
        break;
      }
      counter++;
    }
    return nodeIds.reverse();
  }

  public static getFirstExecutedNodeAtCurrentLayer(executionPath: ExecutionPath): string {
    return this.getLastNExecutedNodes(executionPath, Number.MAX_SAFE_INTEGER)[0];
  }

  public static getShapesFromNodeToChildren(parentNode: Shape): ShapesFromNodeToChildren {
    const parentOutgoingConnection: Connection  = ModelingUtil.getNodeOutgoingConnections(parentNode)[0];
    const gatewayElement: NodeModdleElement = ModelingUtil.getConnectionTarget(parentOutgoingConnection);

    return {
      parentNode: parentNode,
      connectionFromParentToGateway: parentOutgoingConnection,
      gateway: gatewayElement,
      connectionsFromGatewayToChildren: gatewayElement.outgoing,
      children: gatewayElement.outgoing.map(connection => connection.targetRef),
    };
  }

  public static getShapesFromParentToAChild(parentNode: Shape, childNode: Shape): ShapesFromParentToAChild {
    const parentOutgoingConnection: Connection[] = ModelingUtil.getNodeOutgoingConnections(parentNode)[0];
    const gateway = ModelingUtil.getConnectionTarget(parentOutgoingConnection);
    const childIncomingConnection = ModelingUtil.getNodeIncomingConnections(childNode)[0];
    return {
      parentShape: parentNode,
      connectionFromParentToGateway: parentOutgoingConnection,
      gateway: gateway,
      connectionsFromGatewayToAChild: childIncomingConnection,
      childShape: childNode
    };
  }

  public static convertShapesToNodes(shapes: Shape[]) {
      return shapes.map(shape => ExecutedPathUtils.convertShapeToNode(shape));
  }

  public static convertShapeToNode(element: Shape) {
    return {
      nodeId: element.id,
      name: element.businessObject ? element.businessObject.name : element.name,
      questionsAndAnswers: ExecutedPathUtils.getQuestionsAnswers(element),
      nodeType: ModelingUtil.getNodeType(element)
    };
  }

  public static getQuestionsAnswers(element: Shape): QuestionAnswer[] {
    if (element) {
      const nodeDetails = ModelingUtil.getNodeDetails(element);
      return ProcessInputDataUtils.convertNodeDetailsToQuestionAnswers(nodeDetails);
    }
  }

  public static setElementsForSelection(
                          executionPath: ExecutionPath,
                          currentUserTaskId: string,
                          currentTask: Shape,
                          currentSubProcessShape: Shape
  ) {
    const shapesForSelection = ExecutedPathUtils.getShapesFromNodeToChildren(currentSubProcessShape);
    executionPath.gatewayIdToNodesToSelect = shapesForSelection.gateway.id;
    executionPath.connectionsToActivitiesForSelectionIds =
      shapesForSelection.connectionsFromGatewayToChildren.map(connection => connection.id);
    executionPath.connectionsToActivitiesForSelectionIds.push(shapesForSelection.connectionFromParentToGateway.id);
    executionPath.nodesForSelection = ExecutedPathUtils.convertShapesToNodes(shapesForSelection.children);

    executionPath.currentUserTaskHolder = {
      ...{
        userInputActivityId: currentTask.id,
        userInputTaskId: currentUserTaskId
      },
      ...ExecutedPathUtils.convertShapeToNode(currentSubProcessShape)
    };
  }

  public static findNodesAtPathByType(executionPath: ExecutionPath, nodeType: NodeType): Node[] {
    const result: Array<Node>  = new Array();
    if (isEmpty(executionPath)) {
      return result;
    }
    if (executionPath.currentUserTaskHolder && executionPath.currentUserTaskHolder.nodeType === nodeType) {
      result.push(executionPath.currentUserTaskHolder);
    }
    if (!isEmpty(executionPath.executedNodes)) {
      result.push(...(executionPath.executedNodes.filter(node => node.nodeType === nodeType)));
    }

    return result;
  }

  static doesExecutedPathEndAtMainTree(executionPath: ExecutionPath) {
    return executionPath.executedNodes.find(node => node.nodeType === NodeType.neighborhood_entry) === undefined;
  }

  static getPathAtMainTree(executionPath: ExecutionPath) {
    const result: Array<string> = [];
    let index: number = 0;
    while (index < executionPath.executedNodes.length &&
      executionPath.executedNodes[index].nodeType !== NodeType.neighborhood_head) {
      result.push(executionPath.executedNodes[index].nodeId);
      index++;
    }
    return result;
  }
}
